import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, LayoutTemplate, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConversationDTO, WireframeDTO } from '@harness/shared';
import { Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relTime } from '../../lib/format';
import { useConversation, useIntegrations, useIssues, type ProjectDetail } from '../../lib/queries';
import { useCan, VIEWER_HINT } from '../../lib/role';
import { toast } from '../../lib/store';

// Create Wireframes: pick a Jira ticket, watch the Wireframer draw it, type changes, open it full page.
// The preview renders the HTML while it streams in, then the saved version once the turn ends. It's a
// sandboxed iframe (no same-origin): the agent's HTML can't touch the harness.

export const wireframesKey = (convId: string) => ['wireframes', convId] as const;

/** The HTML document in an ```html block, finished or still streaming. */
function streamingHtml(text: string) {
  const m = [...text.matchAll(/```html\s*\n([\s\S]*?)(?:```|$)/gi)].at(-1);
  return m?.[1] && m[1].length > 20 ? m[1] : null;
}

/** Re-render the iframe at most every 350 ms while streaming (no flicker on every token). */
function useThrottled<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  const last = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    const wait = ms - (Date.now() - last.current);
    clearTimeout(timer.current);
    if (wait <= 0) {
      last.current = Date.now();
      setV(value);
    } else
      timer.current = setTimeout(() => {
        last.current = Date.now();
        setV(value);
      }, wait);
    return () => clearTimeout(timer.current);
  }, [value, ms]);
  return v;
}

export default function WireframePanel({ project, convId, drawer }: { project: ProjectDetail; convId?: string; drawer: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const can = useCan();
  const conv = useConversation(convId);
  const integrations = useIntegrations(project.id);
  const jiraLinked = !!integrations.data?.jira;
  const issues = useIssues(project.id, jiraLinked);
  const versions = useQuery({ queryKey: wireframesKey(convId ?? '-'), queryFn: () => api.get<WireframeDTO[]>(`/api/conversations/${convId}/wireframes`), enabled: !!convId });
  const [picked, setPicked] = useState<number | null>(null); // null = latest
  const latest = versions.data?.[0]?.version ?? null;
  const shown = picked ?? latest;
  const saved = useQuery({
    queryKey: ['wireframe-html', convId, shown],
    queryFn: () => api.get<WireframeDTO & { html: string }>(`/api/conversations/${convId}/wireframes/${shown}`),
    enabled: !!convId && shown != null,
    staleTime: Infinity,
  });
  const [key, setKey] = useState('');
  const [starting, setStarting] = useState(false);

  const running = conv.data?.conversation.status === 'running';
  const liveText = useMemo(() => {
    if (!running) return null;
    const last = [...(conv.data?.messages ?? [])].reverse().find((m) => m.role === 'assistant');
    return last ? streamingHtml(last.parts.map((p) => (p.type === 'text' ? p.text : '')).join('\n')) : null;
  }, [running, conv.data?.messages]);
  const live = useThrottled(liveText, 350);
  const html = (running && live) || saved.data?.html || null;
  const jiraKey = conv.data?.conversation.jiraKey ?? null;

  useEffect(() => setPicked(null), [latest]);

  const start = async () => {
    if (!key) return;
    setStarting(true);
    try {
      let id = convId;
      if (!id) {
        const c = await api.post<ConversationDTO>(`/api/projects/${project.id}/conversations`, { mode: 'wireframe' });
        qc.setQueryData<ConversationDTO[]>(qk.conversations(project.id), (l) => [c, ...(l ?? [])]);
        id = c.id;
      }
      await api.post(`/api/conversations/${id}/wireframe/ticket`, { key });
      void qc.invalidateQueries({ queryKey: qk.conversation(id) });
      if (id !== convId) nav(`/p/${project.slug}/c/${id}`);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setStarting(false);
    }
  };

  return (
    <aside className={`card ws-card wf-panel ${drawer ? 'drawer' : ''}`} aria-label="Wireframe preview">
      <div className="wf-bar">
        {jiraLinked && (
          <div className="row" style={{ flexWrap: 'nowrap', gap: 8, flex: 1, minWidth: 0 }}>
            <select className="input" value={key} onChange={(e) => setKey(e.target.value)} aria-label="Jira ticket" style={{ flex: 1, minWidth: 0 }}>
              <option value="">{jiraKey ? `Ticket: ${jiraKey} · pick another…` : 'Pick a Jira ticket…'}</option>
              {(issues.data ?? []).map((i) => (
                <option key={i.key} value={i.key}>
                  {i.key} · {i.summary}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" disabled={!key || starting || running || !can.edit} title={!can.edit ? VIEWER_HINT : undefined} onClick={() => void start()}>
              {starting ? <Spinner /> : <Sparkles className="icon" />}
              {jiraKey ? 'Redo for ticket' : 'Generate'}
            </button>
          </div>
        )}
        {!jiraLinked && <span className="muted" style={{ fontSize: 13, flex: 1 }}>Describe the screen in the chat (link Jira to pick tickets).</span>}
      </div>
      <div className="wf-bar sub">
        <span className="wf-status">
          {running ? (
            <>
              <Spinner /> {live ? 'Drawing…' : 'Reading the code…'}
            </>
          ) : shown != null ? (
            <>
              v{shown}
              {versions.data?.find((v) => v.version === shown) ? ` · ${relTime(versions.data.find((v) => v.version === shown)!.createdAt)}` : ''}
            </>
          ) : (
            'No wireframe yet'
          )}
        </span>
        {(versions.data?.length ?? 0) > 1 && !running && (
          <select className="input" style={{ width: 110 }} value={shown ?? ''} onChange={(e) => setPicked(Number(e.target.value))} aria-label="Version">
            {versions.data!.map((v) => (
              <option key={v.version} value={v.version}>
                v{v.version}
                {v.version === latest ? ' (latest)' : ''}
              </option>
            ))}
          </select>
        )}
        {convId && shown != null && (
          <a className="btn btn-secondary" href={`/api/conversations/${convId}/wireframes/${shown}/view`} target="_blank" rel="noopener noreferrer" title="Open this version full page in a new tab">
            <ExternalLink className="icon" />
            Open in new tab
          </a>
        )}
      </div>
      <div className="wf-stage">
        {html ? (
          <iframe title="Wireframe" sandbox="allow-scripts" srcDoc={html} className="wf-frame" />
        ) : (
          <div className="empty" style={{ margin: 'auto', textAlign: 'center', maxWidth: 360 }}>
            <LayoutTemplate className="icon" style={{ fontSize: 28, opacity: 0.5 }} />
            <div style={{ marginTop: 8 }}>
              {running ? 'The Wireframer is reading the code; the drawing appears here as soon as it starts.' : jiraLinked ? 'Pick a Jira ticket above and press Generate, or describe a screen in the chat.' : 'Describe the screen you want in the chat.'}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
